import React from 'react';
import SEO from '../components/SEO';
import PageHero, { CTABanner } from '../components/PageHero';
import { values, stats } from '../data/mock';
import { Check, Target, Heart, Compass, Award } from 'lucide-react';

export default function AboutPage() {
  return (
    <>
      <SEO
        title="About the Firm"
        description="Salihi Consulting Services is a multidisciplinary advisory firm partnering with governments, enterprises, NGOs and development institutions to strengthen governance, finance, operations and capability."
        path="/about"
        keywords="about Salihi Consulting, multidisciplinary advisory firm, consulting Mogadishu, institutional development, governance advisory"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'About Salihi Consulting Services',
          url: 'https://salihiconsulting.com/about',
          mainEntity: { '@id': 'https://salihiconsulting.com/#organization' },
        }}
      />
      <PageHero
        breadcrumb="About the Firm"
        eyebrow="About Salihi Consulting"
        title="A professional consulting firm built for"
        italicTitle="institutions that cannot afford to get it wrong."
        subtitle="Salihi Consulting Services is a multidisciplinary advisory firm partnering with governments, enterprises, NGOs, and development institutions to navigate complexity, strengthen governance, and unlock growth across Somalia and the wider East-African region."
      />

      {/* Who we are */}
      <section className="py-20 sm:py-24 bg-[#0d2143]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <p className="text-[#e8b863] tracking-[0.28em] text-[11px] font-semibold uppercase mb-5">Who we are</p>
            <h2 className="font-serif text-white font-medium text-[30px] sm:text-[38px] lg:text-[44px] leading-[1.1] tracking-tight">
              An integrated team of advisors, accountants, lawyers, technologists and operators.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-white/78 text-[15.5px] lg:text-[16.5px] leading-relaxed">
            <p>
              Salihi Consulting Services brings together professionals across accounting,
              tax, audit, law, regulation, technology, human resources, procurement,
              project management, and monitoring &amp; evaluation — organized as a single,
              accountable team rather than disconnected practice silos.
            </p>
            <p>
              Our clients range from ministries and public institutions to family-owned
              businesses, multinational corporates, and donor-funded programs. The
              common thread is the seriousness of the work: the kind of engagement where
              precision, discretion, and follow-through are not optional.
            </p>
            <p>
              We work to a simple discipline. Understand the real problem. Design a
              solution that fits the context. Deliver it with the client, not at them.
              Leave behind the capacity to sustain the improvement.
            </p>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Promise */}
      <section className="py-20 sm:py-24 bg-[#0a1b35]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: Compass, label: 'Our Mission', title: 'Make institutions stronger.', body: 'To partner with organizations that shape economies and communities — strengthening their governance, finance, operations, and capability so they can deliver on their mandate.' },
              { icon: Target, label: 'Our Vision', title: 'The most trusted consulting partner in the region.', body: 'To be the most trusted multidisciplinary advisory firm in Somalia and East Africa — known for integrity, technical depth, and outcomes that endure.' },
              { icon: Heart, label: 'Our Promise', title: 'Senior attention, every engagement.', body: 'Every engagement is led by senior professionals, scoped honestly, and delivered against measurable success criteria. No bait-and-switch, no shelf-ware deliverables.' },
            ].map(({ icon: Icon, label, title, body }) => (
              <div key={label} className="bg-gradient-to-br from-white/[0.06] to-white/[0.02] border border-white/10 rounded-2xl p-7 hover:border-[#e8b863]/45 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-[#e8b863]/12 border border-[#e8b863]/30 flex items-center justify-center mb-5">
                  <Icon className="w-5 h-5 text-[#e8b863]" />
                </div>
                <p className="text-[10px] text-[#e8b863] tracking-[0.22em] uppercase font-semibold mb-2">{label}</p>
                <h3 className="font-serif text-white text-[22px] sm:text-[24px] leading-snug mb-3">{title}</h3>
                <p className="text-white/72 text-[14.5px] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 sm:py-24 bg-[#0d2143]">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10">
          <div className="max-w-2xl mb-12">
            <p className="text-[#e8b863] tracking-[0.28em] text-[11px] font-semibold uppercase mb-5">Our values</p>
            <h2 className="font-serif text-white font-medium text-[30px] sm:text-[38px] lg:text-[44px] leading-[1.1] tracking-tight">
              Four principles, applied without exception.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <div key={v.title} className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 rounded-2xl p-6 hover:border-[#e8b863]/40 transition-colors">
                <span className="font-serif text-[#e8b863]/45 text-[36px] leading-none">0{i + 1}</span>
                <h3 className="text-white font-semibold text-[18px] mt-3 mb-3">{v.title}</h3>
                <p className="text-white/72 text-[14px] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="py-16 sm:py-20 bg-[#0a1b35] border-y border-[#e8b863]/12">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center lg:text-left">
              <div className="font-serif text-[42px] sm:text-[52px] lg:text-[60px] leading-none gold-gradient">{s.value}{s.suffix}</div>
              <p className="text-white/72 text-[12.5px] lg:text-[13.5px] mt-3 max-w-[220px] mx-auto lg:mx-0">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What sets us apart */}
      <section className="py-20 sm:py-24 bg-[#0d2143]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-6 lg:px-10">
          <div className="max-w-3xl mb-10">
            <p className="text-[#e8b863] tracking-[0.28em] text-[11px] font-semibold uppercase mb-5">What sets us apart</p>
            <h2 className="font-serif text-white font-medium text-[30px] sm:text-[38px] lg:text-[44px] leading-[1.1] tracking-tight">
              Built differently — on purpose.
            </h2>
          </div>
          <ul className="space-y-4">
            {[
              'A single integrated team across finance, legal, technology, HR, procurement, and M&E — not a network of loosely connected consultants.',
              'Senior-led delivery: the people who scope the work are the people doing the work.',
              'Confidentiality and discretion as a default — trusted with sensitive, high-stakes matters by ministries and major institutions.',
              'Engagement-level transparency on scope, fees and milestones — no surprises, no scope creep.',
              'Knowledge transfer hard-wired into every project so clients are stronger after we leave than before we arrived.',
            ].map((line) => (
              <li key={line} className="flex items-start gap-3.5 bg-gradient-to-br from-white/[0.05] to-white/[0.02] border border-white/10 rounded-2xl p-5 sm:p-6">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#e8b863]/18 border border-[#e8b863]/40 flex items-center justify-center mt-0.5">
                  <Check className="w-3.5 h-3.5 text-[#e8b863]" strokeWidth={3} />
                </span>
                <span className="text-white/82 text-[15px] lg:text-[16px] leading-relaxed">{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
